/**
 * CSV export for the My Account → Patient Payments report.
 *
 * This is the artifact a practice reconciles against their Open Dental deposit
 * at end of day, so two things matter more than they usually would:
 *
 *  1. **The total must be right and must be present.** The office is matching a
 *     number, not browsing rows. The TOTAL footer is part of the deliverable,
 *     not decoration — precedent: the Twilio usage export in SuperAdmin.vue,
 *     which carries its own TOTAL/DISCREPANCY footer.
 *  2. **Escaping cannot be sloppy.** Patient names are "Last, First" — EVERY
 *     row contains a comma. Unescaped, every row would shift a column and the
 *     amounts would land under the wrong headers.
 *
 * Kept as a pure function (rather than inline in the component) so the escaping
 * and the total can be tested without mounting anything.
 */

/**
 * RFC-4180 field escaping: wrap in quotes when the value contains a comma,
 * quote, CR or LF, and double any embedded quotes.
 */
export function csvEscape(value) {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

/**
 * The amount a row actually COLLECTED.
 *
 * `TotalCharged` is what the office asked for; `CapturedAmount` is what the
 * processor approved. They differ on a PARTIAL APPROVAL — an HSA/FSA/prepaid
 * card with less on it than the balance — where the charge succeeds at a
 * smaller amount (Tulip Tree 2026-08-18: $1,709.20 requested, $1,591.34 taken).
 * A reconciliation total built on the requested amount OVER-states the day.
 *
 * `CapturedAmount` is null on every pre-2026-08-18 row and whenever the gateway
 * reported no amount, so the fallback is both required and correct.
 */
export function collectedAmountOf(payment) {
  const captured = parseFloat(payment && payment.CapturedAmount);
  if (Number.isFinite(captured)) return captured;
  const requested = parseFloat(payment && payment.TotalCharged);
  return Number.isFinite(requested) ? requested : null;
}

/** True when the processor approved LESS than the office asked for. */
export function isPartialApproval(payment) {
  const captured = parseFloat(payment && payment.CapturedAmount);
  const requested = parseFloat(payment && payment.TotalCharged);
  return Number.isFinite(captured) && Number.isFinite(requested) && captured < requested;
}

/** Sum of the amounts actually collected, rounded to cents. */
export function sumCharged(payments) {
  const total = (payments || []).reduce((acc, p) => {
    const n = collectedAmountOf(p);
    return acc + (Number.isFinite(n) ? n : 0);
  }, 0);
  return Math.round(total * 100) / 100;
}

/**
 * True when the charge was reversed at the processor after capture (staff
 * pressed Void on the terminal). The row is still shown — an office
 * reconciling its day needs to SEE the void, not find money quietly missing —
 * but it is not a collection and must never land in a total.
 */
export function isCollected(payment) {
  return !payment || payment.Status !== 'voided';
}

/** Sum of only the rows that represent money actually collected. */
export function sumCollected(payments) {
  return sumCharged((payments || []).filter(isCollected));
}

/**
 * OdSyncStatus values that mean a HUMAN has to do something.
 *
 *   'unconfirmed' — dispatched, past the grace window, still not in OD.
 *   'failed'      — the OD write threw before it was ever dispatched.
 *
 * Deliberately EXCLUDES 'pending'. `pending` means "we dispatched it and
 * haven't run the confirming pass yet" — our cadence, not Open Dental's state.
 * The write reaches OD in seconds; reconcileOdPayments only looks once a row is
 * past the grace window, so a healthy payment sits in `pending` for a few
 * minutes by design. Counting that as a problem is what made Summerbrook's very
 * first payment (2026-09-15) read "NEEDS ATTENTION 1 · $2.00 unconfirmed" and
 * "SYNCED TO OD 0%" for the 24 minutes AFTER it had already landed in their
 * Open Dental. See PAYMENT_SYSTEM.md §14.
 *
 * Also excludes `null` — the OD write was never attempted for that row (feature
 * off, or a pre-feature payment). Nothing to chase.
 */
export const OD_PROBLEM_STATUSES = Object.freeze(['unconfirmed', 'failed']);

/**
 * True when a payment's OD-sync state needs someone to look at it.
 * A voided charge is never a problem — OD correctly holds no record of it.
 */
export function isOdProblem(payment) {
  if (!payment || !isCollected(payment)) return false;
  return OD_PROBLEM_STATUSES.includes(payment.OdSyncStatus);
}

export const PAYMENTS_CSV_HEADERS = Object.freeze([
  'Patient', 'PatNum', 'Amount', 'Type', 'Channel', 'Paid', 'Status', 'Open Dental Sync', 'Transaction',
]);

/**
 * Build the CSV text.
 *
 * @param {object} o
 * @param {Array}  o.payments   rows as returned by /od-sync-status-list
 * @param {Function} o.formatPaid  (PaidAt) => string, injected so the caller
 *   supplies the SITE-timezone formatter it already uses on screen — the CSV
 *   must not disagree with the table it was exported from.
 * @param {Function} [o.channelLabel]  (channel) => 'In-office' | 'Link' | '—'
 * @param {Function} [o.syncLabel]     (OdSyncStatus) => human label
 * @returns {string}
 */
export function buildPaymentsCsv({ payments, formatPaid, channelLabel, syncLabel }) {
  const rows = payments || [];
  const fmtPaid = formatPaid || ((v) => (v == null ? '' : String(v)));
  const fmtChannel = channelLabel || ((c) => c || '');
  const fmtSync = syncLabel || ((s) => s || '');

  const lines = [PAYMENTS_CSV_HEADERS.join(',')];

  for (const p of rows) {
    lines.push([
      csvEscape(p.PatientName || (p.PatNum != null ? `PatNum ${p.PatNum}` : '')),
      csvEscape(p.PatNum),
      // Bare number, not "$1,023.30" — a currency-formatted string with a
      // thousands separator is text to Excel and will not sum. The COLLECTED
      // amount, so the column adds up to the TOTAL footer under it.
      csvEscape(Number.isFinite(collectedAmountOf(p)) ? collectedAmountOf(p).toFixed(2) : ''),
      // A treatment-plan deposit posts to OD as unearned, a balance payment as
      // earned — the office needs to tell them apart when reconciling.
      csvEscape(p.PaymentIntent === 'prepayment' ? 'Deposit' : 'Balance'),
      csvEscape(fmtChannel(p.channel)),
      csvEscape(fmtPaid(p.PaidAt)),
      // Voided rows stay in the export so the sheet matches the screen and the
      // processor's batch — but they are excluded from the TOTAL below. A
      // partial approval IS collected money, just less than was asked for, so it
      // counts toward the total and is labelled so the gap is explicable.
      csvEscape(!isCollected(p) ? 'Voided' : (isPartialApproval(p) ? 'Collected (partial)' : 'Collected')),
      csvEscape(fmtSync(p.OdSyncStatus)),
      csvEscape(p.MoolahTransactionId),
    ].join(','));
  }

  // TOTAL footer, aligned under the Amount column.
  lines.push(['TOTAL', '', sumCollected(rows).toFixed(2), '', '', '', '', '', ''].join(','));

  return lines.join('\r\n');
}

/**
 * Filename carrying the range, so a folder of end-of-day exports stays legible.
 * teamio-payments-2026-08-13.csv, or …-2026-08-10_to_2026-08-13.csv.
 */
export function paymentsCsvFilename({ from, to }) {
  if (from && to && from !== to) return `teamio-payments-${from}_to_${to}.csv`;
  const day = from || to;
  return day ? `teamio-payments-${day}.csv` : 'teamio-payments.csv';
}
